import { Check, X } from "lucide-react";
import Image from "next/image";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import AudioButton from "@/features/quiz/components/audio-button";
import { QuestionDropdown } from "@/features/quiz/components/question-dropdown";
import { QuestionStatusCard } from "@/features/quiz/components/question-status-card";
import type { QuestionType } from "@/features/quiz/types";
import { useImagePreviewStore } from "@/lib/hooks/use-preview-image";
import type { QuestionStatus } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";

interface FlashcardProps {
  front: {
    question: string;
    questionImageUrl: string | null;
  };
  back: {
    answer: string;
    answerImageUrl: string | null;
  };
  className?: string;
  backContentStyle?: React.CSSProperties;
  questionStatus?: QuestionStatus;
  height?: string;
  borderRadius?: string;
  width?: string;
  style?: React.CSSProperties;
  onClickTextToSpeech?: () => void;
  timerDuration?: number;
  gradeUserQuestion?: (answer: string, type: QuestionType) => void;
  showGrading?: boolean;
  isFlipped: boolean;
  toggleIsFlipped: () => void;
  activeIndex: number;
  isCorrect: boolean | null;
  studySetId?: string;
  questionId?: string;
  onDeleteSuccess?: () => void;
  questionUserId?: string;
}

const FlashCard = ({
  front,
  back,
  className = "",
  backContentStyle,
  questionStatus,
  height,
  borderRadius = "1rem",
  width,
  style,
  onClickTextToSpeech,
  timerDuration = 10,
  gradeUserQuestion,
  showGrading = false,
  isFlipped,
  toggleIsFlipped,
  activeIndex,
  isCorrect,
  studySetId,
  onDeleteSuccess,
  questionUserId,
  questionId,
}: FlashcardProps) => {
  const [_timeLeft, _setTimeLeft] = useState(timerDuration / 2);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const t = useI18n();
  useEffect(() => {
    const handleSpacePress = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        toggleIsFlipped();
      }
    };

    document.addEventListener("keydown", handleSpacePress);
    return () => document.removeEventListener("keydown", handleSpacePress);
  }, []);

  const speakText = useCallback((text: string) => {
    if ("speechSynthesis" in window) {
      const synth = window.speechSynthesis;
      const speech = new SpeechSynthesisUtterance(text);

      speech.lang = "en-GB";
      speech.rate = 1.0;
      speech.pitch = 1.0;
      speech.volume = 1.0;

      const setupVoice = (voices: SpeechSynthesisVoice[]) => {
        speech.voice =
          voices.find(
            (voice) =>
              voice.lang === "en-GB" &&
              voice.name.includes("Google") &&
              voice.name.includes("Female"),
          ) ||
          voices.find((voice) => voice.lang === "en-GB") ||
          voices[0];

        setIsSpeaking(true);
        speech.onend = () => setIsSpeaking(false);
        speech.onerror = (event) => {
          console.error("Speech synthesis error:", event);
          setIsSpeaking(false);
        };

        synth.speak(speech);
      };

      const voices = synth.getVoices();
      if (voices.length === 0) {
        synth.onvoiceschanged = () => setupVoice(synth.getVoices());
      } else {
        setupVoice(voices);
      }
    } else {
      console.error("Speech synthesis not supported");
    }
  }, []);

  const onManualFlip = () => {
    toggleIsFlipped();
  };

  const handleAudioClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isSpeaking) return;
    event.stopPropagation();
    const textToRead = isFlipped ? back.answer : front.question;
    speakText(textToRead);
    if (onClickTextToSpeech) onClickTextToSpeech();
  };

  return (
    <div
      className={`clear-left w-full h-[300px] md:h-[400px] transform-none relative ${className}`}
      style={{
        perspective: "1000px",
        height,
        width,
        ...style,
      }}
    >
      <div
        className="relative w-full h-full"
        style={{
          transform: isFlipped ? "rotateX(180deg)" : "",
          transformStyle: "preserve-3d",
          transition: "transform 0.45s ease",
          borderRadius,
        }}
        onClick={onManualFlip}
      >
        <CardSide
          content={front.question}
          imageUrl={front.questionImageUrl}
          isFlipped={false}
          isSpeaking={isSpeaking}
          handleAudioClick={handleAudioClick}
          questionStatus={questionStatus}
          studySetId={studySetId}
          questionId={questionId}
          onDeleteSuccess={onDeleteSuccess}
          questionUserId={questionUserId}
        />
        <CardSide
          content={back.answer}
          imageUrl={back.answerImageUrl}
          isFlipped={true}
          isSpeaking={isSpeaking}
          handleAudioClick={handleAudioClick}
          questionStatus={questionStatus}
          backContentStyle={backContentStyle}
          studySetId={studySetId}
          questionId={questionId}
          onDeleteSuccess={onDeleteSuccess}
          questionUserId={questionUserId}
        >
          {showGrading && gradeUserQuestion && (
            <div className="flex justify-center gap-4 px-4 md:px-6 pb-4 md:pb-6 w-full">
              <GradeButton
                onClick={() => gradeUserQuestion("I didn't know", "flashcards")}
                isCorrect={isCorrect}
                label={t("iDontKnow")}
              />
              <GradeButton
                onClick={() => gradeUserQuestion("I know it", "flashcards")}
                isCorrect={isCorrect}
                label={t("iKnowIt")}
              />
            </div>
          )}
        </CardSide>
      </div>
    </div>
  );
};

interface CardSideProps {
  content?: string;
  imageUrl?: string | null;
  isFlipped: boolean;
  isSpeaking: boolean;
  handleAudioClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  questionStatus?: QuestionStatus;
  backContentStyle?: React.CSSProperties;
  children?: React.ReactNode;
  studySetId?: string;
  questionId?: string;
  onDeleteSuccess?: () => void;
  questionUserId?: string;
}

const CardSide: React.FC<CardSideProps> = ({
  content,
  imageUrl,
  isFlipped,
  isSpeaking,
  handleAudioClick,
  questionStatus,
  backContentStyle,
  children,
  studySetId,
  questionId,
  onDeleteSuccess,
  questionUserId,
}) => {
  const openImagePreview = useImagePreviewStore((state) => state.open);
  const t = useI18n();
  const contentPresent = !!content;
  const imagePresent = !!imageUrl;

  return (
    <div
      className="absolute top-0 left-0 w-full h-full cursor-pointer flex flex-col bg-card rounded-2xl border shadow overflow-hidden"
      style={{
        backfaceVisibility: "hidden",
        backgroundColor: "#ffffff",
        transform: isFlipped ? "rotateX(-180deg)" : "",
        ...backContentStyle,
      }}
    >
      {/* Top bar with labels and badges — in normal flow so content never overlaps */}
      <div className="flex items-center justify-between px-4 py-4 md:px-6 md:pt-6 shrink-0">
        <div className="flex gap-1 items-center">
          <p className="text-xs font-medium text-muted-foreground">
            {isFlipped ? t("answer") : t("question")}
          </p>
          <AudioButton question={content || ""} />
        </div>
        <div className="flex items-center gap-2">
          {questionStatus && (
            <QuestionStatusCard questionStatus={questionStatus} />
          )}
          {studySetId && questionId && onDeleteSuccess && questionUserId && (
            <QuestionDropdown
              studySetId={studySetId}
              questionId={questionId}
              questionUserId={questionUserId}
              onDeleteSuccess={onDeleteSuccess}
            />
          )}
        </div>
      </div>

      {/* Main content area — fills remaining space between top bar and buttons */}
      <div
        className={cn(
          "w-full flex-1 min-h-0 flex flex-row items-center px-4 pb-2 md:px-6 md:pb-4 overflow-hidden",
          !contentPresent && imagePresent && "justify-center",
        )}
      >
        {contentPresent && (
          <div className="flex-1 h-full flex flex-col justify-center min-w-0">
            <div className="text-xl md:text-3xl text-foreground">
              <div className="line-clamp-5 font-normal">{content}</div>
            </div>
          </div>
        )}

        {imagePresent && (
          <div
            className={cn(
              "flex items-center justify-center min-w-0 min-h-0 max-h-full",
              contentPresent ? "ml-4 flex-1 max-w-[50%]" : "max-w-full",
            )}
          >
            <div
              className="relative cursor-zoom-in max-h-full"
              style={{
                borderRadius: "0.8rem",
                overflow: "hidden",
              }}
              onClick={(e) => {
                e.stopPropagation();
                openImagePreview(imageUrl, content || "");
              }}
            >
              <Image
                src={imageUrl}
                height={150}
                width={150}
                alt={content || "Card image"}
                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAoHBwgHBgoICAgLCgoLDhgQDg0NDh0VFhEYIx8lJCIfIiEmKzcvJik0KSEiMEExNDk7Pj4+JS5ESUM8SDc9Pjv/2wBDAQoLCw4NDhwQEBw7KCIoOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozv/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWEREiMxUf/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8ACS5RvG9fPC7sZWWNd53kY5GOgBIGgG9N4JIsSPJFGITJIVQLgAnAwKtSlBmxsR0A0AoooQv1O0RRQCJf/9k="
                placeholder="blur"
                style={{
                  color: "transparent",
                  objectFit: "scale-down",
                  cursor: "zoom-in",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  height: "auto",
                  width: "auto",
                }}
              />
            </div>
          </div>
        )}
      </div>

      {children}
    </div>
  );
};

const GradeButton: React.FC<{
  onClick: () => void;
  isCorrect: boolean | null;
  label?: string;
}> = ({ onClick, isCorrect, label }) => {
  const t = useI18n();
  return (
    <Button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "flex flex-1 h-auto items-center justify-center py-2 px-3 hover:bg-card bg-card shadow-sm border rounded-lg",
        isCorrect && label === t("iKnowIt")
          ? "border-success"
          : isCorrect === false && label === t("iDontKnow")
            ? "border-destructive"
            : "hover:border-brand-primary",
      )}
    >
      <div className="flex items-center justify-center w-full">
        <span
          className={cn(
            "p-2 w-8 hidden md:flex items-center justify-center h-8 rounded-md text-center text-sm font-medium flex-none text-foreground",
            isCorrect && label === t("iKnowIt")
              ? "bg-success/10 text-success"
              : isCorrect === false && label === t("iDontKnow")
                ? "bg-destructive/10 text-destructive"
                : "bg-muted",
          )}
        >
          {isCorrect && label === t("iKnowIt") ? (
            <Check className="size-4" />
          ) : isCorrect === false && label === t("iDontKnow") ? (
            <X className="size-4" />
          ) : label === t("iDontKnow") ? (
            1
          ) : (
            2
          )}
        </span>
        {label && (
          <span className="md:ml-3 text-center text-foreground">{label}</span>
        )}
      </div>
    </Button>
  );
};

export default FlashCard;
